/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-28
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Button,
} from '@mui/material';
import { Business, Person, Schedule, Email } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { invitationApi } from '../../../api/invitationApi';
import { API_BASE_URL } from '../../../config/api';
import {
  InvitationDetail,
  InvitationError,
  InvitationResponse,
} from '../../../types/invitation';
import {
  isRateLimitedUserCheck,
  UserDetectionService,
} from '../../../utils/userDetection';
import { useEmailVerificationRequestMutation } from '../../../features/auth';
import TurnstileWidget from '../Waitlist/waitlist/TurnstileWidget';
import ExistingUserInvitationForm from './ExistingUserInvitationForm';
import NewUserInvitationForm from './NewUserInvitationForm';
import InvitationDeclineForm from './InvitationDeclineForm';

/**
 * Main invitation detail page component.
 * Displays invitation details and handles accept/decline flows.
 */
const InvitationDetailPage: React.FC = () => {
  const { t } = useTranslation('studio');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState<InvitationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<InvitationError | null>(null);
  const [currentView, setCurrentView] = useState<
    'details' | 'accept' | 'decline' | 'success'
  >('details');
  const [userExists, setUserExists] = useState<boolean | null>(null);
  const [checkingUser, setCheckingUser] = useState(false);

  // Success state tracking
  const [successData, setSuccessData] = useState<{
    isNewUser: boolean;
    userEmail?: string;
  } | null>(null);

  // Email verification state for new users
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  // Email verification API
  const [resendVerification, { isLoading: isResendLoading }] =
    useEmailVerificationRequestMutation();

  const loadInvitationDetails = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const details = await invitationApi.getInvitationDetails(token!);
      console.log('[InvitationDetailPage] Loaded invitation details:', details);
      console.log(
        '[InvitationDetailPage] Studio logo URL:',
        details.studio_logo
      );

      // Fix relative URL for studio logo - prepend API base URL if needed.
      // Use runtime API base so test deploys point at test API, not prod.
      if (details.studio_logo && !details.studio_logo.startsWith('http')) {
        const apiBaseUrl = API_BASE_URL;
        // Ensure the path starts with /media/
        let logoPath = details.studio_logo;
        if (!logoPath.startsWith('/')) {
          logoPath = `/${logoPath}`;
        }
        if (!logoPath.startsWith('/media/')) {
          logoPath = `/media${logoPath}`;
        }
        const originalUrl = details.studio_logo;
        details.studio_logo = `${apiBaseUrl}${logoPath}`;
        console.log('[InvitationDetailPage] Original URL:', originalUrl);
        console.log(
          '[InvitationDetailPage] Fixed logo URL:',
          details.studio_logo
        );
      }

      setInvitation(details);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      // Determine error type based on message
      let errorType: InvitationError['type'] = 'server';
      if (errorMessage.includes('Invalid') || errorMessage.includes('404')) {
        errorType = 'invalid_token';
      } else if (
        errorMessage.includes('expired') ||
        errorMessage.includes('410')
      ) {
        errorType = 'expired';
      } else if (
        errorMessage.includes('network') ||
        errorMessage.includes('fetch')
      ) {
        errorType = 'network';
      }

      setError({
        message: errorMessage,
        type: errorType,
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError({
        message: 'Invalid invitation link',
        type: 'invalid_token',
      });
      setLoading(false);
      return;
    }

    loadInvitationDetails();
  }, [token, loadInvitationDetails]);

  // Handle user detection when accepting invitation
  const handleAcceptClick = async () => {
    if (!invitation) return;

    setCheckingUser(true);
    setError(null);

    try {
      const exists = await UserDetectionService.checkUserExists(
        invitation.email
      );
      setUserExists(exists);
      setCurrentView('accept');
    } catch (err) {
      // Never fall back to the sign-up form when the check itself failed.
      setError({
        message: t(
          isRateLimitedUserCheck(err)
            ? 'invitationDetailPage.errors.rateLimited'
            : 'invitationDetailPage.errors.checkFailed'
        ),
        type: 'validation',
      });
    } finally {
      setCheckingUser(false);
    }
  };

  const handleAcceptSuccess = (
    _response: InvitationResponse,
    isNewUser: boolean = false,
    userEmail?: string
  ) => {
    setSuccessData({ isNewUser, userEmail });
    setCurrentView('success');

    // Different redirect timing based on user type
    const redirectDelay = isNewUser ? 8000 : 3000; // 8 seconds for new users to read email verification info
    setTimeout(() => {
      navigate('/dashboard');
    }, redirectDelay);
  };

  const handleBackToDetails = () => {
    setCurrentView('details');
    setUserExists(null);
    setError(null);
  };

  const handleDeclineSuccess = () => {
    setCurrentView('success');
    // Redirect to home page after 3 seconds
    setTimeout(() => {
      navigate('/');
    }, 3000);
  };

  const handleError = (errorMessage: string) => {
    setError({
      message: errorMessage,
      type: 'validation',
    });
  };

  const formatExpirationDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getExpirationColor = (expiresInDays: number) => {
    if (expiresInDays <= 1) return 'error';
    if (expiresInDays <= 3) return 'warning';
    return 'success';
  };

  // Handle resend verification email for new users
  const handleResendEmail = async () => {
    if (!successData?.userEmail) {
      setResendError(t('invitationDetailPage.success.newUser.resendError'));
      return;
    }

    if (!turnstileToken) {
      setResendError(t('invitationDetailPage.success.newUser.turnstileError'));
      return;
    }

    try {
      await resendVerification({
        email: successData.userEmail,
        turnstile_token: turnstileToken,
      }).unwrap();
      setResendSuccess(true);
      setResendError('');
    } catch (err: any) {
      setResendError(
        err.data?.message ||
          t('invitationDetailPage.success.newUser.resendError')
      );
      setResendSuccess(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
          gap={2}
        >
          <CircularProgress size={50} />
          <Typography variant="h6" color="text.secondary">
            {t('invitationDetailPage.loading')}
          </Typography>
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {error.type === 'invalid_token' &&
                  t('invitationDetailPage.errors.invalidToken')}
                {error.type === 'expired' &&
                  t('invitationDetailPage.errors.expired')}
                {error.type === 'network' &&
                  t('invitationDetailPage.errors.network')}
                {error.type === 'server' &&
                  t('invitationDetailPage.errors.server')}
                {error.type === 'validation' &&
                  t('invitationDetailPage.errors.validation')}
              </Typography>
              <Typography variant="body2">{error.message}</Typography>
            </Alert>

            {error.type === 'network' && (
              <Box textAlign="center" mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('invitationDetailPage.errors.networkDescription')}
                </Typography>
              </Box>
            )}

            {(error.type === 'invalid_token' || error.type === 'expired') && (
              <Box textAlign="center" mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {error.type === 'expired'
                    ? t('invitationDetailPage.errors.expiredDescription')
                    : t('invitationDetailPage.errors.invalidDescription')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Success state
  if (currentView === 'success') {
    const isNewUser = successData?.isNewUser || false;
    const userEmail = successData?.userEmail;

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            {isNewUser ? (
              // New User Success with Email Verification
              <Box sx={{ py: 4 }}>
                <Box textAlign="center" mb={4}>
                  <Email sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h4" color="primary" gutterBottom>
                    {t('invitationDetailPage.success.newUser.title')}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {t('invitationDetailPage.success.newUser.subtitle')}
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    {t('invitationDetailPage.success.newUser.verifyEmail')}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t('invitationDetailPage.success.newUser.sentTo')}
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                    {userEmail}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {t('invitationDetailPage.success.newUser.instructions')}
                  </Typography>

                  {resendSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {t('invitationDetailPage.success.newUser.resendSuccess')}
                    </Alert>
                  )}

                  {resendError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {resendError}
                    </Alert>
                  )}

                  <Box sx={{ mb: 3 }}>
                    <TurnstileWidget
                      onSuccess={setTurnstileToken}
                      onError={() =>
                        setResendError('Verification failed. Please try again.')
                      }
                      onExpire={() => setTurnstileToken('')}
                      disabled={isResendLoading}
                    />
                  </Box>

                  <Button
                    variant="outlined"
                    onClick={handleResendEmail}
                    disabled={isResendLoading || !userEmail || !turnstileToken}
                    sx={{ mb: 3 }}
                  >
                    {isResendLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      t('invitationDetailPage.success.newUser.resendButton')
                    )}
                  </Button>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box textAlign="center">
                  <Typography variant="body1" color="text.secondary">
                    {t(
                      'invitationDetailPage.success.newUser.redirectingDashboard'
                    )}
                  </Typography>
                </Box>
              </Box>
            ) : (
              // Existing User Success
              <Box textAlign="center" py={4}>
                <Typography variant="h4" color="primary" gutterBottom>
                  {t('invitationDetailPage.success.title')}
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {t('invitationDetailPage.success.subtitle')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('invitationDetailPage.success.redirecting')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Main invitation details view
  if (!invitation) {
    return null;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Invitation Details Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t('invitationDetailPage.header.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('invitationDetailPage.header.subtitle')}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Studio Information */}
          <Box mb={3}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar
                src={invitation.studio_logo || undefined}
                sx={{ width: 60, height: 60 }}
                imgProps={{
                  onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
                    console.error(
                      '[InvitationDetailPage] Failed to load studio logo:',
                      invitation.studio_logo,
                      e
                    );
                  },
                  onLoad: () => {
                    console.log(
                      '[InvitationDetailPage] Successfully loaded studio logo:',
                      invitation.studio_logo
                    );
                  },
                }}
              >
                <Business />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h2">
                  {invitation.studio_name}
                </Typography>
                {invitation.studio_description && (
                  <Typography variant="body2" color="text.secondary">
                    {invitation.studio_description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Invitation Details */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              {t('invitationDetailPage.details.heading')}
            </Typography>

            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Email color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('invitationDetailPage.details.invitedEmail')}
                  </Typography>
                  <Typography variant="body1">{invitation.email}</Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <Person color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('invitationDetailPage.details.role')}
                  </Typography>
                  <Chip
                    label={invitation.role_display}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <Person color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('invitationDetailPage.details.invitedBy')}
                  </Typography>
                  <Typography variant="body1">
                    {invitation.invited_by_name}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <Schedule color="action" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('invitationDetailPage.details.expires')}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">
                      {formatExpirationDate(invitation.invitation_expires_at)}
                    </Typography>
                    <Chip
                      label={t('invitationDetailPage.details.daysLeft', {
                        days: invitation.expires_in_days,
                        count: invitation.expires_in_days,
                      })}
                      color={getExpirationColor(invitation.expires_in_days)}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          {currentView === 'details' && (
            <Box display="flex" gap={2} justifyContent="center" mt={4}>
              <Box
                component="button"
                onClick={handleAcceptClick}
                disabled={checkingUser}
                sx={{
                  px: 4,
                  py: 1.5,
                  backgroundColor: 'primary.main',
                  color: 'white',
                  border: 'none',
                  borderRadius: 1,
                  cursor: checkingUser ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'medium',
                  opacity: checkingUser ? 0.6 : 1,
                  '&:hover': {
                    backgroundColor: checkingUser
                      ? 'primary.main'
                      : 'primary.dark',
                  },
                }}
              >
                {checkingUser ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {t('invitationDetailPage.actions.checking')}
                  </>
                ) : (
                  t('invitationDetailPage.actions.accept')
                )}
              </Box>
              <Box
                component="button"
                onClick={() => setCurrentView('decline')}
                sx={{
                  px: 4,
                  py: 1.5,
                  backgroundColor: 'transparent',
                  color: 'text.secondary',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'medium',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {t('invitationDetailPage.actions.decline')}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Accept Form - Conditional based on user existence */}
      {currentView === 'accept' && userExists === true && (
        <ExistingUserInvitationForm
          invitation={invitation}
          token={token!}
          onSuccess={handleAcceptSuccess}
          onError={handleError}
          onBack={handleBackToDetails}
        />
      )}

      {currentView === 'accept' && userExists === false && (
        <NewUserInvitationForm
          invitation={invitation}
          token={token!}
          onSuccess={handleAcceptSuccess}
          onError={handleError}
          onBack={handleBackToDetails}
        />
      )}

      {/* Loading state while checking user */}
      {currentView === 'accept' && userExists === null && (
        <Card>
          <CardContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={4}
              gap={2}
            >
              <CircularProgress size={40} />
              <Typography variant="h6" color="text.secondary">
                {t('invitationDetailPage.actions.checkingAccount')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('invitationDetailPage.actions.checkingDescription')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Decline Form */}
      {currentView === 'decline' && (
        <InvitationDeclineForm
          invitation={invitation}
          token={token!}
          onSuccess={handleDeclineSuccess}
          onError={handleError}
          onBack={handleBackToDetails}
        />
      )}
    </Container>
  );
};

export default InvitationDetailPage;
